<template>
  <WorkspaceRoleDenied v-if="staffDenied" :area="t('integracao.area')" />
  <div v-else class="workspace-chatbot-page">
    <div class="modern-header">
      <div class="header-container">
        <div class="breadcrumb-section">
          <nav class="breadcrumb">
            <button type="button" class="breadcrumb-item" @click="router.push('/dashboard')">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
              </svg>
              {{ t('common.breadcrumb.dashboard') }}
            </button>
            <span class="breadcrumb-separator">/</span>
            <button type="button" class="breadcrumb-item" @click="router.push(`/workspace/${workspaceId}`)">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
              </svg>
              {{ currentWorkspace?.nome || t('workspaceDashboard.defaultName') }}
            </button>
            <span class="breadcrumb-separator">/</span>
            <button type="button" class="breadcrumb-item" @click="goBack">
              {{ t('integracao.chatbot.breadcrumbIntegracao') }}
            </button>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">{{ t('integracao.chatbot.breadcrumbCurrent') }}</span>
          </nav>
        </div>

        <div class="title-section">
          <div class="title-content">
            <div class="title-icon title-icon--whatsapp">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
              </svg>
            </div>
            <div class="title-text">
              <h1 class="main-title">{{ t('integracao.chatbot.title') }}</h1>
              <p class="subtitle">{{ t('integracao.chatbot.subtitle') }}</p>
            </div>
          </div>
          <div class="chatbot-status-badge" :class="{ active: ativo }">
            <span class="status-dot" />
            {{ ativo ? t('integracao.chatbot.badgeActive') : t('integracao.chatbot.badgeInactive') }}
          </div>
        </div>

        <div class="actions-section">
          <button type="button" class="action-button secondary" @click="goBack">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            {{ t('integracao.chatbot.back') }}
          </button>
          <button
            type="button"
            class="action-button primary"
            :disabled="saving || loading"
            @click="save"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            {{ saving ? t('integracao.saving') : t('integracao.chatbot.savePublish') }}
          </button>
        </div>
      </div>
    </div>

    <div class="page-content">
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner" />
        <p class="loading-text">{{ t('common.loading') }}</p>
      </div>

      <template v-else>
        <section class="config-panel">
          <header class="config-panel__header">
            <div>
              <h2 class="config-panel__title">{{ t('integracao.chatbot.settingsTitle') }}</h2>
              <p class="config-panel__subtitle">{{ t('integracao.chatbot.settingsSubtitle') }}</p>
            </div>
          </header>

          <div class="config-grid">
            <article class="config-tile config-tile--enable" :class="{ 'config-tile--on': ativo }">
              <div class="config-tile__head">
                <div class="config-tile__icon config-tile__icon--green">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                  </svg>
                </div>
                <span class="config-pill" :class="ativo ? 'config-pill--on' : 'config-pill--off'">
                  {{ ativo ? t('integracao.chatbot.badgeActive') : t('integracao.chatbot.badgeInactive') }}
                </span>
              </div>
              <h3 class="config-tile__title">{{ t('integracao.chatbot.enable') }}</h3>
              <p class="config-tile__desc">{{ t('integracao.chatbot.enableHint') }}</p>
              <div class="config-tile__foot">
                <label class="switch">
                  <input v-model="ativo" type="checkbox" />
                  <span class="switch-slider" />
                </label>
              </div>
            </article>

            <article class="config-tile">
              <div class="config-tile__head">
                <div class="config-tile__icon config-tile__icon--purple">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                </div>
              </div>
              <h3 class="config-tile__title">{{ t('integracao.chatbot.flowName') }}</h3>
              <p class="config-tile__desc">{{ t('integracao.chatbot.flowNameHint') }}</p>
              <div class="config-tile__foot">
                <input id="flow-name" v-model="nome" type="text" class="input config-tile__input" />
              </div>
            </article>

            <article class="config-tile">
              <div class="config-tile__head">
                <div class="config-tile__icon config-tile__icon--amber">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
              </div>
              <h3 class="config-tile__title">{{ t('integracao.chatbot.startStep') }}</h3>
              <p class="config-tile__desc">{{ t('integracao.chatbot.startStepHint') }}</p>
              <div class="config-tile__foot">
                <select id="start-step" v-model="editor.startNodeId" class="input config-tile__input">
                  <option v-for="s in editor.steps" :key="s.id" :value="s.id">{{ s.id }}</option>
                </select>
              </div>
            </article>

            <article class="config-tile config-tile--notify" :class="{ 'config-tile--on': endAttendanceNotifyEnabled }">
              <div class="config-tile__head">
                <div class="config-tile__icon config-tile__icon--blue">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                  </svg>
                </div>
                <span class="config-pill" :class="endAttendanceNotifyEnabled ? 'config-pill--on' : 'config-pill--off'">
                  {{ endAttendanceNotifyEnabled ? t('integracao.chatbot.endAttendanceNotifyEnabled') : t('integracao.chatbot.endAttendanceNotifyDisabled') }}
                </span>
              </div>
              <h3 class="config-tile__title">{{ t('integracao.chatbot.endAttendanceNotifyTitle') }}</h3>
              <p class="config-tile__desc">{{ t('integracao.chatbot.endAttendanceNotifyDesc') }}</p>
              <div class="config-tile__foot config-tile__foot--stack">
                <label class="switch">
                  <input v-model="endAttendanceNotifyEnabled" type="checkbox" />
                  <span class="switch-slider" />
                </label>
                <template v-if="endAttendanceNotifyEnabled">
                  <button type="button" class="btn-edit-end-msg" @click="openEndNotifyModal">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                    {{ t('integracao.chatbot.editEndAttendanceMessage') }}
                  </button>
                  <div v-if="endAttendanceNotifyPreview" class="end-msg-snippet">
                    <span class="end-msg-snippet__label">{{ t('integracao.chatbot.preview') }}</span>
                    <p>{{ endAttendanceNotifyPreview }}</p>
                  </div>
                </template>
              </div>
            </article>
          </div>
        </section>

        <div class="workspace-editor">
          <aside class="steps-sidebar card-surface">
            <div class="sidebar-head">
              <div>
                <h2>{{ t('integracao.chatbot.steps') }}</h2>
                <p class="sidebar-meta">{{ t('integracao.chatbot.stepsCount', { n: editor.steps.length }) }}</p>
              </div>
              <button type="button" class="btn-add-step" @click="openAddStepModal">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                </svg>
                {{ t('integracao.chatbot.addStep') }}
              </button>
            </div>
            <ul class="steps-list">
              <li
                v-for="(step, idx) in editor.steps"
                :key="step.id"
                class="step-list-item"
                :class="{ active: selectedStepId === step.id, 'is-start': editor.startNodeId === step.id }"
                @click="selectedStepId = step.id"
              >
                <div class="step-list-icon" :class="step.type === 'menu' ? 'menu' : 'message'">
                  <svg v-if="step.type === 'menu'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7"/>
                  </svg>
                  <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                  </svg>
                </div>
                <div class="step-list-info">
                  <span class="step-list-id">{{ step.id }}</span>
                  <span class="step-list-type">
                    {{ step.type === 'menu' ? t('integracao.chatbot.typeMenuShort') : t('integracao.chatbot.typeMessageShort') }}
                  </span>
                </div>
                <span v-if="editor.startNodeId === step.id" class="step-start-pill">{{ t('integracao.chatbot.startStepBadge') }}</span>
                <button
                  v-if="editor.steps.length > 1"
                  type="button"
                  class="step-list-remove"
                  :title="t('common.remove')"
                  @click.stop="removeStep(idx)"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </li>
            </ul>
          </aside>

          <main class="step-editor card-surface">
            <template v-if="selectedStep">
              <div class="editor-head">
                <div>
                  <h2>{{ t('integracao.chatbot.editStep') }}</h2>
                  <code class="step-code">{{ selectedStep.id }}</code>
                </div>
                <div class="type-pills">
                  <button
                    type="button"
                    class="type-pill"
                    :class="{ active: selectedStep.type === 'menu' }"
                    @click="selectedStep.type = 'menu'"
                  >
                    {{ t('integracao.chatbot.typeMenu') }}
                  </button>
                  <button
                    type="button"
                    class="type-pill"
                    :class="{ active: selectedStep.type === 'message' }"
                    @click="selectedStep.type = 'message'"
                  >
                    {{ t('integracao.chatbot.typeMessage') }}
                  </button>
                </div>
              </div>

              <div class="field-block">
                <label>{{ t('integracao.chatbot.messageText') }}</label>
                <textarea v-model="selectedStep.text" rows="4" class="input input--textarea" :placeholder="t('integracao.chatbot.messageText')" />
              </div>

              <div v-if="selectedStep.type === 'message'" class="field-block">
                <label>{{ t('integracao.chatbot.nextStep') }}</label>
                <select v-model="selectedStep.nextNodeId" class="input">
                  <option value="">{{ t('integracao.chatbot.endFlow') }}</option>
                  <option :value="HANDOFF_NEXT">{{ t('integracao.chatbot.actionHandoff') }}</option>
                  <option v-for="s in editor.steps" :key="'n-' + s.id" :value="s.id">{{ s.id }}</option>
                </select>
              </div>

              <div v-if="selectedStep.type === 'menu'" class="options-section">
                <div class="options-head">
                  <h3>{{ t('integracao.chatbot.options') }}</h3>
                  <button type="button" class="btn-ghost" @click="addOption(selectedStep)">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                    </svg>
                    {{ t('common.add') }}
                  </button>
                </div>
                <div class="options-table-head">
                  <span>{{ t('integracao.chatbot.optionNumber') }}</span>
                  <span>{{ t('integracao.chatbot.optionLabel') }}</span>
                  <span>{{ t('integracao.chatbot.optionAction') }}</span>
                  <span>{{ t('integracao.chatbot.optionTarget') }}</span>
                  <span />
                </div>
                <div
                  v-for="(opt, oi) in selectedStep.options"
                  :key="oi"
                  class="option-card"
                >
                  <input v-model="opt.id" class="input input--compact" :placeholder="t('integracao.chatbot.optNumber')" />
                  <input v-model="opt.label" class="input input--compact" :placeholder="t('integracao.chatbot.optLabel')" />
                  <select v-model="opt.action" class="input input--compact">
                    <option value="goto">{{ t('integracao.chatbot.actionGoto') }}</option>
                    <option value="handoff">{{ t('integracao.chatbot.actionHandoff') }}</option>
                    <option value="end">{{ t('integracao.chatbot.actionEnd') }}</option>
                  </select>
                  <select
                    v-if="opt.action === 'goto'"
                    v-model="opt.targetId"
                    class="input input--compact"
                  >
                    <option value="">{{ t('integracao.chatbot.selectStep') }}</option>
                    <option v-for="s in editor.steps" :key="'t-' + s.id" :value="s.id">{{ s.id }}</option>
                  </select>
                  <span v-else class="option-target-empty">—</span>
                  <button type="button" class="btn-icon-danger" :title="t('common.remove')" @click="removeOption(selectedStep, oi)">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </div>
            </template>
            <div v-else class="editor-empty">
              <p>{{ t('integracao.chatbot.selectStepHint') }}</p>
            </div>
          </main>

          <aside class="preview-aside">
            <div class="preview-card card-surface">
              <div class="preview-card-head">
                <h2>{{ t('integracao.chatbot.preview') }}</h2>
                <p>{{ t('integracao.chatbot.previewHint') }}</p>
              </div>
              <div class="wa-phone">
                <div class="wa-phone-top">
                  <div class="wa-avatar" />
                  <span>{{ t('integracao.chatbot.title') }}</span>
                </div>
                <div class="wa-chat-bg">
                  <div class="wa-bubble">
                    <pre class="wa-bubble-text">{{ previewContent }}</pre>
                    <span class="wa-time">{{ previewTime }}</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </template>
    </div>

    <div v-if="showEndNotifyModal" class="modal-overlay" @click="closeEndNotifyModal">
      <div class="modal-content modal-content--wide" @click.stop>
        <div class="modal-header">
          <h3>{{ t('integracao.chatbot.endAttendanceMessageModalTitle') }}</h3>
          <button type="button" class="modal-close" :aria-label="t('common.close')" @click="closeEndNotifyModal">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <form class="modal-body" @submit.prevent="confirmEndNotifyModal">
          <p class="modal-hint">{{ t('integracao.chatbot.endAttendanceMessageModalHint') }}</p>
          <div class="field-block">
            <label for="end-notify-msg">{{ t('integracao.chatbot.messageText') }}</label>
            <textarea
              id="end-notify-msg"
              ref="endNotifyMessageInput"
              v-model="endNotifyDraft"
              rows="5"
              class="input input--textarea-modal"
              :placeholder="t('integracao.chatbot.endAttendanceNotifyPlaceholder')"
              @input="endNotifyError = ''"
            />
            <p v-if="endNotifyError" class="field-error">{{ endNotifyError }}</p>
          </div>
        </form>
        <div class="modal-footer">
          <button type="button" class="btn-modal secondary" @click="closeEndNotifyModal">
            {{ t('common.cancel') }}
          </button>
          <button type="button" class="btn-modal primary" @click="confirmEndNotifyModal">
            {{ t('common.confirm') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showAddStepModal" class="modal-overlay" @click="closeAddStepModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ t('integracao.chatbot.addStepModalTitle') }}</h3>
          <button type="button" class="modal-close" :aria-label="t('common.close')" @click="closeAddStepModal">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <form class="modal-body" @submit.prevent="confirmAddStep">
          <p class="modal-hint">{{ t('integracao.chatbot.addStepModalHint') }}</p>
          <div class="field-block">
            <label for="new-step-id">{{ t('integracao.chatbot.stepId') }}</label>
            <input
              id="new-step-id"
              ref="newStepIdInput"
              v-model="newStepId"
              type="text"
              class="input"
              :placeholder="t('integracao.chatbot.stepIdPlaceholder')"
              autocomplete="off"
              @input="stepIdError = ''"
            />
            <p v-if="stepIdError" class="field-error">{{ stepIdError }}</p>
          </div>
        </form>
        <div class="modal-footer">
          <button type="button" class="btn-modal secondary" @click="closeAddStepModal">
            {{ t('common.cancel') }}
          </button>
          <button type="button" class="btn-modal primary" @click="confirmAddStep">
            {{ t('common.add') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import api from '@/services/api'
import Swal from 'sweetalert2'
import WorkspaceRoleDenied from '@/components/workspace/WorkspaceRoleDenied.vue'
import { useWorkspaceStaffGuard } from '@/composables/useWorkspaceStaffGuard'
import { useGlobalWorkspaces } from '@/composables/useGlobalWorkspaces'
import {
  definitionToEditor,
  editorToDefinition,
  defaultStartStep,
  renderMenuPreview,
  normalizeStepId,
  isValidStepId,
  HANDOFF_NEXT
} from '@/utils/chatbotFlowEditor'

export default {
  name: 'WhatsAppChatbotPage',
  components: { WorkspaceRoleDenied },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()
    const workspaceId = Number(route.params.id)
    const { staffDenied } = useWorkspaceStaffGuard(t('integracao.area'))
    const { currentWorkspace } = useGlobalWorkspaces()

    const loading = ref(true)
    const saving = ref(false)
    const ativo = ref(false)
    const nome = ref('Fluxo principal')
    const endAttendanceNotifyEnabled = ref(false)
    const endAttendanceNotifyMessage = ref('')
    const editor = ref({
      startNodeId: 'start',
      steps: [defaultStartStep()]
    })
    const selectedStepId = ref('start')
    const showAddStepModal = ref(false)
    const newStepId = ref('')
    const stepIdError = ref('')
    const newStepIdInput = ref(null)
    const showEndNotifyModal = ref(false)
    const endNotifyDraft = ref('')
    const endNotifyError = ref('')
    const endNotifyMessageInput = ref(null)

    const endAttendanceNotifyPreview = computed(() => {
      const text = (endAttendanceNotifyMessage.value || '').trim()
      if (!text) return ''
      return text.length > 80 ? `${text.slice(0, 80)}…` : text
    })

    const selectedStep = computed(() =>
      editor.value.steps.find((s) => s.id === selectedStepId.value) || null
    )

    const previewContent = computed(() => {
      const step = selectedStep.value
        || editor.value.steps.find((s) => s.id === editor.value.startNodeId)
        || editor.value.steps[0]
      if (!step) return ''
      if (step.type === 'menu') {
        return renderMenuPreview(step)
      }
      return (step.text || '').trim() || '…'
    })

    const previewTime = computed(() => {
      const now = new Date()
      return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
    })

    watch(
      () => editor.value.steps.map((s) => s.id),
      (ids) => {
        if (!ids.includes(selectedStepId.value)) {
          selectedStepId.value = editor.value.startNodeId || ids[0] || 'start'
        }
      }
    )

    const load = async () => {
      loading.value = true
      try {
        const { data } = await api.get(`/whatsapp/workspace/${workspaceId}/chatbot`)
        ativo.value = !!data.ativo
        nome.value = data.nome || 'Fluxo principal'
        endAttendanceNotifyEnabled.value = !!data.endAttendanceNotifyEnabled
        endAttendanceNotifyMessage.value = data.endAttendanceNotifyMessage || ''
        editor.value = definitionToEditor(data.definitionJson)
        selectedStepId.value = editor.value.startNodeId || editor.value.steps[0]?.id || 'start'
      } catch (e) {
        console.error(e)
        Swal.fire(t('integracao.swal.error'), e.response?.data?.error || e.message, 'error')
      } finally {
        loading.value = false
      }
    }

    const save = async () => {
      saving.value = true
      try {
        const definitionJson = editorToDefinition(editor.value)
        const payload = {
          nome: nome.value,
          ativo: ativo.value,
          triggerType: 'FIRST_CONTACT',
          welcomeDelaySeconds: 0,
          definitionJson,
          endAttendanceNotifyEnabled: endAttendanceNotifyEnabled.value,
          endAttendanceNotifyMessage: endAttendanceNotifyEnabled.value
            ? endAttendanceNotifyMessage.value
            : ''
        }
        await api.put(`/whatsapp/workspace/${workspaceId}/chatbot`, payload)
        Swal.fire(t('integracao.swal.success'), t('integracao.chatbot.saved'), 'success')
      } catch (e) {
        Swal.fire(t('integracao.swal.error'), e.response?.data?.error || e.message, 'error')
      } finally {
        saving.value = false
      }
    }

    const openEndNotifyModal = () => {
      endNotifyDraft.value = endAttendanceNotifyMessage.value || ''
      endNotifyError.value = ''
      showEndNotifyModal.value = true
      nextTick(() => endNotifyMessageInput.value?.focus())
    }

    const closeEndNotifyModal = () => {
      showEndNotifyModal.value = false
      endNotifyDraft.value = ''
      endNotifyError.value = ''
    }

    const confirmEndNotifyModal = () => {
      const text = endNotifyDraft.value.trim()
      if (!text) {
        endNotifyError.value = t('integracao.chatbot.endAttendanceMessageRequired')
        return
      }
      endAttendanceNotifyMessage.value = text
      closeEndNotifyModal()
    }

    const openAddStepModal = () => {
      newStepId.value = ''
      stepIdError.value = ''
      showAddStepModal.value = true
      nextTick(() => newStepIdInput.value?.focus())
    }

    const closeAddStepModal = () => {
      showAddStepModal.value = false
      newStepId.value = ''
      stepIdError.value = ''
    }

    const confirmAddStep = () => {
      const id = normalizeStepId(newStepId.value)
      if (!id) {
        stepIdError.value = t('integracao.chatbot.stepIdRequired')
        return
      }
      if (!isValidStepId(id)) {
        stepIdError.value = t('integracao.chatbot.stepIdInvalid')
        return
      }
      const exists = editor.value.steps.some((s) => s.id === id)
      if (exists) {
        stepIdError.value = t('integracao.chatbot.stepIdDuplicate')
        return
      }
      editor.value.steps.push({
        id,
        type: 'message',
        text: '',
        nextNodeId: '',
        options: []
      })
      selectedStepId.value = id
      closeAddStepModal()
    }

    const removeStep = (idx) => {
      const removed = editor.value.steps[idx]
      editor.value.steps.splice(idx, 1)
      if (editor.value.startNodeId === removed.id) {
        editor.value.startNodeId = editor.value.steps[0]?.id || 'start'
      }
      if (selectedStepId.value === removed.id) {
        selectedStepId.value = editor.value.startNodeId
      }
    }

    const addOption = (step) => {
      const nextNum = String((step.options?.length || 0) + 1)
      if (!step.options) step.options = []
      step.options.push({ id: nextNum, label: '', action: 'goto', targetId: '' })
    }

    const removeOption = (step, oi) => {
      step.options.splice(oi, 1)
    }

    const goBack = () => {
      router.push({ name: 'WorkspaceIntegracao', params: { id: workspaceId } })
    }

    onMounted(load)

    return {
      t,
      router,
      workspaceId,
      currentWorkspace,
      staffDenied,
      loading,
      saving,
      ativo,
      nome,
      endAttendanceNotifyEnabled,
      endAttendanceNotifyMessage,
      endAttendanceNotifyPreview,
      showEndNotifyModal,
      endNotifyDraft,
      endNotifyError,
      endNotifyMessageInput,
      openEndNotifyModal,
      closeEndNotifyModal,
      confirmEndNotifyModal,
      editor,
      selectedStepId,
      selectedStep,
      previewContent,
      previewTime,
      save,
      showAddStepModal,
      newStepId,
      stepIdError,
      newStepIdInput,
      openAddStepModal,
      closeAddStepModal,
      confirmAddStep,
      removeStep,
      addOption,
      removeOption,
      goBack,
      HANDOFF_NEXT
    }
  }
}
</script>

<style scoped>
.workspace-chatbot-page {
  --chatbot-radius: 4px;
  width: 100%;
  min-height: 100vh;
  background-color: var(--bg-primary);
  color: var(--text-primary);
}

.modern-header {
  width: 100%;
  background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%);
  border-bottom: 1px solid var(--border-primary);
  padding: 24px 0;
  box-sizing: border-box;
}

.header-container {
  width: 100%;
  padding: 0 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.breadcrumb-section { display: flex; align-items: center; }

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  flex-wrap: wrap;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.breadcrumb-item:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.breadcrumb-separator { color: var(--text-muted); margin: 0 2px; }
.breadcrumb-current { color: var(--text-primary); font-weight: 600; }

.title-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.title-content { display: flex; align-items: center; gap: 16px; }

.title-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 12px;
  color: white;
}

.title-icon--whatsapp {
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  box-shadow: 0 8px 24px rgba(37, 211, 102, 0.35);
}

.title-text { display: flex; flex-direction: column; gap: 4px; }
.main-title { font-size: 28px; font-weight: 700; margin: 0; }
.subtitle { font-size: 15px; color: var(--text-secondary); margin: 0; }

.chatbot-status-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-primary);
}

.chatbot-status-badge.active {
  background: color-mix(in srgb, #25d366 15%, var(--bg-secondary));
  color: #25d366;
  border-color: color-mix(in srgb, #25d366 40%, transparent);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--text-muted);
}

.chatbot-status-badge.active .status-dot {
  background: #25d366;
  box-shadow: 0 0 8px rgba(37, 211, 102, 0.8);
}

.actions-section { display: flex; gap: 12px; flex-wrap: wrap; }

.action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-button.primary {
  background: linear-gradient(135deg, var(--accent-primary) 0%, #7c3aed 100%);
  color: white;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35);
}

.action-button.primary:hover:not(:disabled) {
  transform: translateY(-1px);
}

.action-button.secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}

.action-button:disabled { opacity: 0.6; cursor: not-allowed; }

.page-content {
  width: 100%;
  padding: 24px;
  box-sizing: border-box;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 64px;
  gap: 16px;
}

.loading-spinner {
  width: 44px;
  height: 44px;
  border: 3px solid var(--border-primary);
  border-top-color: var(--accent-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* Painel de configuração */
.config-panel {
  margin-bottom: 28px;
  padding: 24px 28px 28px;
  background: linear-gradient(160deg, var(--bg-secondary) 0%, color-mix(in srgb, var(--bg-tertiary) 80%, var(--bg-secondary)) 100%);
  border: 1px solid var(--border-primary);
  border-radius: var(--chatbot-radius);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 8px 32px rgba(0, 0, 0, 0.06);
}

.config-panel__header {
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid color-mix(in srgb, var(--border-primary) 80%, transparent);
}

.config-panel__title {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.02em;
}

.config-panel__subtitle {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.45;
  max-width: 560px;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

@media (max-width: 900px) {
  .config-grid { grid-template-columns: 1fr; }
}

.config-tile {
  display: flex;
  flex-direction: column;
  min-height: 200px;
  padding: 20px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: var(--chatbot-radius);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.config-tile:hover {
  border-color: color-mix(in srgb, var(--accent-primary) 25%, var(--border-primary));
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.07);
  transform: translateY(-1px);
}

.config-tile--on {
  border-color: color-mix(in srgb, #25d366 45%, var(--border-primary));
  box-shadow: 0 0 0 1px color-mix(in srgb, #25d366 20%, transparent), 0 8px 28px rgba(37, 211, 102, 0.12);
}

.config-tile--enable.config-tile--on {
  background: linear-gradient(145deg, color-mix(in srgb, #25d366 6%, var(--bg-primary)) 0%, var(--bg-primary) 55%);
}

.config-tile--notify.config-tile--on {
  border-color: color-mix(in srgb, #3b82f6 40%, var(--border-primary));
  box-shadow: 0 0 0 1px color-mix(in srgb, #3b82f6 15%, transparent), 0 8px 28px rgba(59, 130, 246, 0.1);
}

.config-tile__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.config-tile__icon {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--chatbot-radius);
  flex-shrink: 0;
}

.config-tile__icon--green {
  background: linear-gradient(135deg, color-mix(in srgb, #25d366 22%, transparent), color-mix(in srgb, #128c7e 12%, transparent));
  color: #128c7e;
}

.config-tile__icon--purple {
  background: linear-gradient(135deg, color-mix(in srgb, var(--accent-primary) 22%, transparent), color-mix(in srgb, #7c3aed 10%, transparent));
  color: var(--accent-primary);
}

.config-tile__icon--amber {
  background: linear-gradient(135deg, color-mix(in srgb, #f59e0b 22%, transparent), color-mix(in srgb, #d97706 10%, transparent));
  color: #d97706;
}

.config-tile__icon--blue {
  background: linear-gradient(135deg, color-mix(in srgb, #3b82f6 22%, transparent), color-mix(in srgb, #2563eb 10%, transparent));
  color: #2563eb;
}

.config-pill {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 4px 10px;
  border-radius: 999px;
  flex-shrink: 0;
}

.config-pill--on {
  background: color-mix(in srgb, #25d366 18%, transparent);
  color: #15803d;
  border: 1px solid color-mix(in srgb, #25d366 35%, transparent);
}

.config-tile--notify .config-pill--on {
  background: color-mix(in srgb, #3b82f6 18%, transparent);
  color: #1d4ed8;
  border-color: color-mix(in srgb, #3b82f6 35%, transparent);
}

.config-pill--off {
  background: var(--bg-tertiary);
  color: var(--text-muted);
  border: 1px solid var(--border-primary);
}

.config-tile__title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.3;
}

.config-tile__desc {
  margin: 0;
  flex: 1;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-secondary);
}

.config-tile__foot {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px dashed color-mix(in srgb, var(--border-primary) 90%, transparent);
  display: flex;
  align-items: center;
}

.config-tile__foot--stack {
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}

.config-tile__input {
  width: 100%;
}

.btn-edit-end-msg {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  border: 1px solid color-mix(in srgb, #3b82f6 35%, var(--border-primary));
  border-radius: var(--chatbot-radius);
  background: color-mix(in srgb, #3b82f6 8%, var(--bg-primary));
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.btn-edit-end-msg:hover {
  background: color-mix(in srgb, #3b82f6 16%, var(--bg-primary));
  border-color: #3b82f6;
}

.end-msg-snippet {
  padding: 10px 12px;
  border-radius: var(--chatbot-radius);
  background: var(--bg-secondary);
  border-left: 3px solid #3b82f6;
}

.end-msg-snippet__label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  margin-bottom: 4px;
}

.end-msg-snippet p {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: var(--text-secondary);
  font-style: italic;
  word-break: break-word;
}

.modal-content--wide {
  max-width: 520px;
}

.input--textarea-modal {
  min-height: 120px;
  resize: vertical;
  line-height: 1.5;
}

.switch {
  position: relative;
  width: 48px;
  height: 26px;
  flex-shrink: 0;
}

.switch input { opacity: 0; width: 0; height: 0; }

.switch-slider {
  position: absolute;
  inset: 0;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 999px;
  cursor: pointer;
  transition: 0.25s;
}

.switch-slider::before {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  left: 2px;
  bottom: 2px;
  background: white;
  border-radius: 50%;
  transition: 0.25s;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.switch input:checked + .switch-slider {
  background: linear-gradient(135deg, #25d366, #128c7e);
  border-color: transparent;
}

.switch input:checked + .switch-slider::before {
  transform: translateX(22px);
}

.card-surface {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: var(--chatbot-radius);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.workspace-editor {
  display: grid;
  grid-template-columns: 280px 1fr 340px;
  gap: 20px;
  align-items: start;
}

@media (max-width: 1400px) {
  .workspace-editor {
    grid-template-columns: 260px 1fr;
  }
  .preview-aside {
    grid-column: 1 / -1;
  }
}

@media (max-width: 900px) {
  .workspace-editor {
    grid-template-columns: 1fr;
  }
}

.steps-sidebar {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 280px);
  position: sticky;
  top: 24px;
}

.sidebar-head {
  padding: 18px 16px;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sidebar-head h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
}

.sidebar-meta {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--text-secondary);
}

.btn-add-step {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px;
  border: 1px dashed color-mix(in srgb, #25d366 50%, var(--border-primary));
  border-radius: var(--chatbot-radius);
  background: color-mix(in srgb, #25d366 8%, transparent);
  color: #128c7e;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add-step:hover {
  background: color-mix(in srgb, #25d366 16%, transparent);
  border-style: solid;
}

.steps-list {
  list-style: none;
  margin: 0;
  padding: 8px;
  overflow-y: auto;
  flex: 1;
}

.step-list-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: var(--chatbot-radius);
  cursor: pointer;
  transition: background 0.15s;
  margin-bottom: 4px;
  border: 1px solid transparent;
}

.step-list-item:hover {
  background: var(--bg-hover);
}

.step-list-item.active {
  background: color-mix(in srgb, var(--accent-primary) 12%, var(--bg-primary));
  border-color: color-mix(in srgb, var(--accent-primary) 35%, transparent);
}

.step-list-item.is-start .step-list-id::after {
  content: none;
}

.step-list-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--chatbot-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.step-list-icon.menu {
  background: color-mix(in srgb, #8b5cf6 18%, var(--bg-primary));
  color: #8b5cf6;
}

.step-list-icon.message {
  background: color-mix(in srgb, #3b82f6 18%, var(--bg-primary));
  color: #3b82f6;
}

.step-list-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-list-id {
  font-size: 13px;
  font-weight: 600;
  font-family: ui-monospace, monospace;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.step-list-type {
  font-size: 11px;
  color: var(--text-secondary);
}

.step-start-pill {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: var(--chatbot-radius);
  background: color-mix(in srgb, #f59e0b 20%, transparent);
  color: #d97706;
  flex-shrink: 0;
}

.step-list-remove {
  flex-shrink: 0;
  padding: 4px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--chatbot-radius);
  opacity: 0;
  transition: opacity 0.15s, color 0.15s, background 0.15s;
}

.step-list-item:hover .step-list-remove,
.step-list-item.active .step-list-remove {
  opacity: 1;
}

.step-list-remove:hover {
  color: #ef4444;
  background: color-mix(in srgb, #ef4444 12%, transparent);
}

.step-editor {
  padding: 24px;
  min-height: 400px;
}

.editor-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.editor-head h2 {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 700;
}

.step-code {
  font-size: 13px;
  padding: 4px 10px;
  border-radius: var(--chatbot-radius);
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  color: var(--accent-primary);
}

.type-pills {
  display: flex;
  gap: 6px;
  padding: 4px;
  background: var(--bg-primary);
  border-radius: var(--chatbot-radius);
  border: 1px solid var(--border-primary);
}

.type-pill {
  padding: 8px 14px;
  border: none;
  border-radius: var(--chatbot-radius);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: var(--text-secondary);
  background: transparent;
  transition: all 0.2s;
  white-space: nowrap;
}

.type-pill.active {
  background: var(--bg-secondary);
  color: var(--text-primary);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.field-block {
  margin-bottom: 20px;
}

.field-block label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.input {
  width: 100%;
  padding: 10px 14px;
  border-radius: var(--chatbot-radius);
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.input:focus {
  outline: none;
  border-color: var(--accent-primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent-primary) 20%, transparent);
}

.input--inline { margin-top: 6px; }
.input--textarea { resize: vertical; min-height: 100px; line-height: 1.5; }
.input--compact { padding: 8px 10px; font-size: 13px; border-radius: var(--chatbot-radius); }

.options-section {
  margin-top: 8px;
  padding-top: 20px;
  border-top: 1px solid var(--border-primary);
}

.options-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.options-head h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
}

.btn-ghost {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: var(--chatbot-radius);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-ghost:hover { background: var(--bg-hover); }

.options-table-head {
  display: grid;
  grid-template-columns: 56px 1fr 130px 1fr 36px;
  gap: 8px;
  padding: 0 4px 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
}

.option-card {
  display: grid;
  grid-template-columns: 56px 1fr 130px 1fr 36px;
  gap: 8px;
  align-items: center;
  padding: 10px;
  margin-bottom: 8px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: var(--chatbot-radius);
  transition: border-color 0.2s;
}

.option-card:hover {
  border-color: color-mix(in srgb, var(--accent-primary) 25%, var(--border-primary));
}

.option-target-empty {
  color: var(--text-muted);
  font-size: 14px;
  text-align: center;
}

.btn-icon-danger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--chatbot-radius);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.15s;
}

.btn-icon-danger:hover {
  background: color-mix(in srgb, #ef4444 15%, transparent);
  color: #ef4444;
}

.editor-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  color: var(--text-secondary);
  font-size: 15px;
}

.preview-aside {
  position: sticky;
  top: 24px;
}

.preview-card {
  padding: 20px;
  overflow: hidden;
}

.preview-card-head {
  margin-bottom: 16px;
}

.preview-card-head h2 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
}

.preview-card-head p {
  margin: 0;
  font-size: 12px;
  color: var(--text-secondary);
}

.wa-phone {
  border-radius: var(--chatbot-radius);
  overflow: hidden;
  border: 3px solid #1f2c34;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  background: #0b141a;
}

.wa-phone-top {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: #1f2c34;
  color: #e9edef;
  font-size: 14px;
  font-weight: 600;
}

.wa-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #25d366, #128c7e);
}

.wa-chat-bg {
  min-height: 280px;
  max-height: 420px;
  overflow-y: auto;
  padding: 16px;
  background-color: #0b141a;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.wa-bubble {
  max-width: 92%;
  margin-left: auto;
  background: #005c4b;
  border-radius: 12px 12px 4px 12px;
  padding: 10px 12px 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  position: relative;
}

.wa-bubble-text {
  margin: 0;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.45;
  color: #e9edef;
  white-space: pre-wrap;
  word-break: break-word;
}

.wa-time {
  display: block;
  text-align: right;
  font-size: 11px;
  color: rgba(233, 237, 239, 0.6);
  margin-top: 4px;
}

@media (max-width: 768px) {
  .options-table-head,
  .option-card {
    grid-template-columns: 1fr;
  }
  .options-table-head { display: none; }
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: var(--bg-secondary);
  border-radius: var(--chatbot-radius);
  border: 1px solid var(--border-primary);
  max-width: 440px;
  width: 100%;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 22px;
  border-bottom: 1px solid var(--border-primary);
}

.modal-header h3 { margin: 0; font-size: 18px; font-weight: 700; }

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--text-secondary);
  cursor: pointer;
}

.modal-close:hover { background: var(--bg-hover); color: var(--text-primary); }

.modal-body { padding: 22px; }
.modal-hint { margin: 0 0 16px; font-size: 14px; color: var(--text-secondary); line-height: 1.5; }
.field-error { margin: 8px 0 0; font-size: 13px; color: #ef4444; }

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 22px;
  border-top: 1px solid var(--border-primary);
}

.btn-modal {
  padding: 10px 18px;
  border-radius: var(--chatbot-radius);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
}

.btn-modal.secondary {
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}

.btn-modal.primary {
  background: linear-gradient(135deg, var(--accent-primary) 0%, #7c3aed 100%);
  color: #fff;
}
</style>
